import React from 'react';
import { Container, Typography, Box, TextField, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import profile from '../assets/profile.png';
import logo from '../assets/logo0.png';

const Hardware = () => {
  const sections = [
    {
      title: "1. Supported Devices & Hardware",
      content: [
        "Mobile Phones: iPhones, Android devices, screen damage, battery issues, charging ports",
        "Computers & Laptops: Hardware diagnostics, overheating, RAM issues, motherboard failures",
        "Household Appliances: Smart TVs, washing machines, refrigerators, microwaves",
        "Gaming Consoles: PlayStation, Xbox, controllers, VR devices",
        "Smart Devices: Smartwatches, tablets, IoT devices"
      ]
    },
    {
      title: "2. AI-Powered Hardware Diagnostics",
      content: "Our advanced AI system analyzes device images to identify hardware failures, screen issues, and component malfunctions with high accuracy. Get real-time diagnostics and repair recommendations."
    },
    {
      title: "3. Spare Parts & Compatibility",
      content: [
        "Verified Parts: Access our marketplace of genuine replacement components",
        "Compatibility Check: AI-powered part matching system",
        "Quality Assurance: All parts verified by our expert team",
        "Direct Shipping: Fast delivery of compatible components"
      ]
    },
    {
      title: "4. Hardware Repair Assistance",
      content: [
        "Step-by-Step Guides: Detailed repair instructions with visual aids",
        "Video Consultations: Connect with expert technicians",
        "Remote Diagnostics: AI-powered problem identification",
        "Repair History: Track all your device repairs and maintenance"
      ]
    },
    {
      title: "5. Security & Quality Control",
      content: "We maintain strict quality control measures for all hardware diagnostics and repairs. Our AI system is regularly updated to ensure accurate fault detection and repair recommendations."
    }
  ];

  const tableOfContents = [
    { id: 'supported-devices', title: '1. Supported Devices' },
    { id: 'ai-diagnostics', title: '2. AI Diagnostics' },
    { id: 'spare-parts', title: '3. Spare Parts' },
    { id: 'repair-assistance', title: '4. Repair Assistance' },
    { id: 'security', title: '5. Security & Quality' }
  ];

  const TableOfContents = () => (
    <Box sx={{ 
      position: 'fixed',
      right: '5%',
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      gap: 2
    }}>
      <Typography sx={{
        color: '#000000',
        fontSize: '16px',
        fontWeight: 600,
        mb: 2
      }}>
        Table of content
      </Typography>
      {tableOfContents.map((item, index) => (
        <Box
          key={index}
          onClick={() => document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' })}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': {
              '& .arrow': {
                opacity: 1,
                width: '24px',
              },
              '& .dot': {
                bgcolor: '#000000',
              },
              '& .title': {
                opacity: 1,
              }
            }
          }}
        >
          <Box className="title" sx={{
            opacity: 0,
            transition: 'all 0.3s ease',
            whiteSpace: 'nowrap',
            color: '#000000',
            fontSize: '14px',
            mr: 1,
            fontWeight: 500
          }}>
            {item.title}
          </Box>
          <Box className="arrow" sx={{
            opacity: 0,
            width: 0,
            height: '2px',
            bgcolor: '#000000',
            transition: 'all 0.3s ease',
            mr: 1
          }} />
          <Box className="dot" sx={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            bgcolor: '#666666',
            transition: 'all 0.3s ease'
          }} />
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <Box sx={{ 
        bgcolor: '#b3b3ff', 
        minHeight: '100vh' 
      }}>
        <Container maxWidth="lg">
          {/* Header Section */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            position: 'relative',
            pt: 8
          }}>
            <Box sx={{ maxWidth: '600px' }}>
              <Typography variant="h1" sx={{ 
                fontSize: '64px',
                fontWeight: 700,
                color: '#000000',
                mb: 3,
                fontFamily: 'serif'
              }}>
                Hardware Support
              </Typography>

              <Typography sx={{ 
                fontSize: '18px',
                color: '#333333',
                mb: 4,
                lineHeight: 1.6
              }}>
                Discover our comprehensive hardware support system powered by AI technology. We provide advanced diagnostics and repair solutions for a wide range of devices, ensuring reliable and efficient repairs.
              </Typography>

              {/* Search Box */}
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                maxWidth: '800px'
              }}>
                <TextField
                  fullWidth
                  placeholder="Comment pouvons-nous vous aider ?"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#ffffff',
                      height: '56px',
                      '& fieldset': {
                        borderColor: '#e0e0e0'
                      }
                    }
                  }}
                />
                <IconButton 
                  sx={{ 
                    position: 'absolute',
                    right: 8,
                    bgcolor: '#000000',
                    color: '#ffffff',
                    '&:hover': {
                      bgcolor: '#333333'
                    }
                  }}
                >
                  <SearchIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Right Image */}
            <Box sx={{ 
              position: 'absolute',
              right: 0,
              top: '-40px'
            }}>
              <img 
                src={profile} 
                alt="Support" 
                style={{
                  height: '500px',
                  objectFit: 'cover'
                }}
              />
            </Box>
          </Box>

          {/* Add Table of Contents */}
          <TableOfContents />

          {/* Hardware Content */}
          <Box sx={{ maxWidth: '800px', mt: 8, mb: 8 }}>
            {sections.map((section, index) => (
              <Box 
                key={index} 
                id={tableOfContents[index].id}
                sx={{ mb: 5 }}
              >
                <Typography variant="h5" sx={{ 
                  color: '#000000',
                  fontWeight: 600,
                  mb: 3
                }}>
                  {section.title}
                </Typography>
                {Array.isArray(section.content) ? (
                  section.content.map((item, i) => (
                    <Typography key={i} sx={{ 
                      color: '#333333',
                      mb: 2,
                      pl: 2
                    }}>
                      • {item}
                    </Typography>
                  ))
                ) : (
                  <Typography sx={{ 
                    color: '#333333',
                    mb: 2,
                    pl: 2
                  }}>
                    {section.content}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Black Footer with White Logo */}
      <Box sx={{ 
        bgcolor: '#000000', 
        py: 4,
        borderTop: '1px solid #333333'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <img 
              src={logo} 
              alt="Larrvarris Logo" 
              style={{
                height: '30px',
                filter: 'brightness(0) invert(1)'
              }}
            />
            <Typography sx={{ color: '#ffffff', fontSize: '14px' }}>
              © 2024 Larrvarris AI Solutions. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Hardware;